export const PLATFORM_PERMISSION_MATRIX: Record<string, readonly string[]> = {
  'platform-owner': ['*'],
  'platform-admin': [
    'platform:companies:view', 'platform:companies:create',
    'platform:companies:update', 'platform:companies:suspend',
    'platform:subscriptions:view', 'platform:subscriptions:manage',
    'platform:billing:view',
    'platform:templates:view', 'platform:templates:manage',
    'platform:procedures:view', 'platform:procedures:manage',
    'platform:platform-users:view',
    'platform:audit-log:view',
    'platform:system:view', 'platform:system:manage',
    'platform:jobs:view', 'platform:jobs:manage',
    'platform:support:view', 'platform:support:manage',
    'platform:impersonation:approve', 'platform:impersonation:view-log',
  ],
  'platform-operations': [
    'platform:companies:view',
    'platform:subscriptions:view',
    'platform:templates:view', 'platform:procedures:view',
    'platform:system:view',
    'platform:jobs:view',
    'platform:support:view', 'platform:support:manage',
  ],
  'platform-finance': [
    'platform:companies:view',
    'platform:subscriptions:view', 'platform:subscriptions:manage',
    'platform:billing:view', 'platform:billing:manage',
  ],
  'platform-support': [
    'platform:companies:view',
    'platform:templates:view', 'platform:procedures:view',
    'platform:support:view', 'platform:support:manage',
    'platform:impersonation:request',
  ],
  'platform-developer': [
    'platform:system:view', 'platform:system:manage',
    'platform:jobs:view', 'platform:jobs:manage',
  ],
  'platform-auditor': [
    'platform:companies:view',
    'platform:subscriptions:view',
    'platform:billing:view',
    'platform:templates:view', 'platform:procedures:view',
    'platform:platform-users:view',
    'platform:audit-log:view',
    'platform:system:view',
    'platform:jobs:view',
    'platform:support:view',
    'platform:impersonation:view-log',
  ],
} as const;

/**
 * UI GUIDANCE ONLY. The backend `PlatformPermissionGuard` is the authority
 * (Phase 12.1B, D-041/D-042) and resolves the same matrix server-side on every
 * request. This helper decides what to *draw*; it never decides what is
 * *allowed*.
 *
 * Phase 12.2A (F-02) — this now FAILS CLOSED.
 *
 * It previously opened with `if (!role) return true`, a deliberate pre-12.1B
 * accommodation for tokens that carried no role. Combined with the
 * `platformRole`/`role` field mismatch it meant the role was ALWAYS undefined,
 * so every platform user saw every navigation item and every gated action —
 * then met a 403 from the backend on the ones they could not use.
 *
 * An absent, empty or unrecognised role now grants NOTHING. That is the correct
 * direction of failure for a UI gate: showing too little is a cosmetic problem,
 * showing too much misrepresents the user's authority.
 *
 * The matrix itself is unchanged, no permission was added, and no role was
 * altered — the frontend was made consistent with the backend, not the reverse.
 */
export function canPlatform(role: string | undefined | null, permission: string): boolean {
  if (typeof role !== 'string' || role.length === 0) return false;

  const permissions = PLATFORM_PERMISSION_MATRIX[role];
  if (permissions === undefined) return false;

  return permissions.includes('*') || permissions.includes(permission);
}
